import { createStableObjectHash, getFixedOptions } from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { useCallback, useMemo } from 'react';

export type GroupedOptionList<T> = {
  selected: Array<SelectItem<T>>;
  notSelected: Array<SelectItem<T>>;
};

type UseMultiLevelSelectOptionsParams<T> = {
  options: SelectItem<T>[];
  unfilteredOptions?: SelectItem<T>[];
  fixedOptions: SelectItem<T>[];
  value: SelectItem<T>[];
  memoizedValueList: SelectItem<T>[];
  searchInput: string | undefined;
  selectedOption: SelectItem<T> | undefined;
  onChange: (selectedValue: SelectItem<T>[]) => void;
  setSelectedOption: (option: SelectItem<T> | undefined) => void;
};

/** Compute a stable string key for a SelectItem value (handles objects and primitives). */
function valueKey<T>(val: T): string {
  return typeof val === 'object' && val !== null ? createStableObjectHash(val) : String(val);
}

export function useMultiLevelSelectOptions<T>({
  options,
  unfilteredOptions,
  fixedOptions,
  value,
  memoizedValueList,
  searchInput,
  selectedOption,
  onChange,
  setSelectedOption,
}: UseMultiLevelSelectOptionsParams<T>) {
  const searchResults = useMemo(() => {
    if (!searchInput) return fixedOptions;
    const lowerSearch = searchInput.toLowerCase();
    return fixedOptions.filter(({ label: optionLabel }) => {
      const labelText = String(optionLabel).toLowerCase();
      return labelText.includes(lowerSearch);
    });
  }, [fixedOptions, searchInput]);

  const selectedValueSet = useMemo(
    () => new Set(memoizedValueList.map(selectedValueItem => valueKey(selectedValueItem.value))),
    [memoizedValueList],
  );

  const groupedOptions = useMemo(() => {
    return searchResults.reduce<GroupedOptionList<T>>(
      (acc, currentOption) => {
        if (selectedValueSet.has(valueKey(currentOption.value))) acc.selected.push(currentOption);
        else acc.notSelected.push(currentOption);
        return acc;
      },
      { selected: [], notSelected: [] },
    );
  }, [searchResults, selectedValueSet]);

  const isOptionSelected = useCallback(
    (option: SelectItem<T>) => selectedValueSet.has(valueKey(option.value)),
    [selectedValueSet],
  );

  /** Pre-compute a Set of value keys for O(1) lookups in handleClickOnCheckbox. */
  const valueKeySet = useMemo(
    () => new Map(value.map(valueListItem => [valueKey(valueListItem.value), valueListItem])),
    [value],
  );

  const handleClickOnCheckbox = useCallback(
    (option: SelectItem<T>) => {
      const optionKey = valueKey(option.value);
      if (valueKeySet.has(optionKey)) {
        return onChange(value.filter(valueListItem => valueKey(valueListItem.value) !== optionKey));
      }
      setSelectedOption(option);
    },
    [value, valueKeySet, onChange, setSelectedOption],
  );

  const categoriesToDisplay = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ category: string; index: number }> = [];
    groupedOptions.notSelected.forEach((cur, index) => {
      const category = cur.category || 'undefined';
      if (!seen.has(category)) {
        seen.add(category);
        result.push({ category, index });
      }
    });
    return result;
  }, [groupedOptions.notSelected]);

  const dropdownButtonFixedOptions = useMemo(
    () => getFixedOptions(options, unfilteredOptions),
    [options, unfilteredOptions],
  );

  /** Pre-index available options by key for O(1) lookups in dropdownButtonValue. */
  const optionsByKey = useMemo(() => {
    const map = new Map<string, SelectItem<T>>();
    for (const option of dropdownButtonFixedOptions) {
      map.set(valueKey(option.value), option);
    }
    return map;
  }, [dropdownButtonFixedOptions]);

  const dropdownButtonValue = useMemo((): SelectItem<T>[] => {
    if (value.length === 0) return [];
    return value.map(currentValue => {
      const currentOption = optionsByKey.get(valueKey(currentValue.value));
      return { ...(currentOption ?? currentValue), level: currentValue.level };
    });
  }, [optionsByKey, value]);

  const handleSelectLevel = useCallback(
    (level: number) => {
      if (!selectedOption) return;
      const optionKey = valueKey(selectedOption.value);
      const updatedValue = value.filter(valueListItem => valueKey(valueListItem.value) !== optionKey);
      updatedValue.push({ ...selectedOption, level });
      onChange(updatedValue);
      setSelectedOption(undefined);
    },
    [value, onChange, selectedOption, setSelectedOption],
  );

  return {
    searchResults,
    groupedOptions,
    isOptionSelected,
    handleClickOnCheckbox,
    categoriesToDisplay,
    dropdownButtonValue,
    handleSelectLevel,
  };
}
